import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useEffect } from "react";

import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { Logo } from "Shared/Logo";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";

type Props = {
  title: string;
  body: string;
};

/**
 * Generic "coming soon" placeholder page used for static pages whose final
 * content is still pending (Privacy Policy, Terms of Service, Support).
 * See docs/technical/mobile-store-deployment-guide.md for the TODO list.
 */
export const PlaceholderPage = ({ title, body }: Props): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  useEffect(() => {
    document.title = `${title} | ${t(Resources.Common.AppName)}`;
  }, [title, t]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.paper",
        px: Spacing.M,
        py: Spacing.L,
      }}
    >
      <Box sx={{ maxWidth: 760, mx: "auto" }}>
        <Box mb={Spacing.L}>
          <Logo size="md" />
        </Box>
        <Stack spacing={Spacing.M} sx={{ color: "text.primary" }}>
          <Typography variant="h1">{title}</Typography>
          <Typography variant="h3" color="text.secondary">
            {t(Resources.Common.ComingSoon)}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {body}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t(Resources.Common.ComingSoonNote)}
          </Typography>
          <Box>
            <UnstyledLink isUnderlined to={AppRouting.SignIn.path()}>
              <Typography variant="body2" color={theme.palette.primary.main}>
                {t(Resources.Common.BackToSignIn)}
              </Typography>
            </UnstyledLink>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};
